/**
 * Money this seat has committed to THIS hand, as the server reads it.
 *
 * Mirrors `toAmount` in shared/games/common/consentQuorum.js: the FOLD/JOINED
 * markers and anything unparseable are 0, because `p{n}bet` carries a marker
 * prefix rather than a plain number when the seat is out of the hand.
 */
export const stakeAmount = (raw?: string): number => {
  const text = String(raw ?? '');
  const marker = text.substring(0, 1);

  if (marker === 'F' || marker === 'J') return 0;

  const parsed = parseFloat(text);

  return Number.isFinite(parsed) ? parsed : 0;
};

/**
 * EN-663: the money half of the server's `isSeatContestingPot` predicate.
 *
 * Without it a client gate is strictly looser than the server's, and the gap is
 * reachable with no tampering: a chipless seat left at status 0/1 after busting
 * — or any seat sitting out, which reads as neither waiting nor folded — has bet
 * and ante both '0' while still seated, so it was offered the vote and then
 * refused with `403 Not in this hand`. The panel has no way to surrender a vote
 * it already rendered as cast, so that player watched a window it was never
 * entered into expire. Offering only what the server will accept is the fix.
 *
 * Lives here rather than inside `UserActions` because there are TWO gates that
 * must agree: the prompt itself, and `useActiveTables`' cross-table vote signal
 * (EN-10237), which advertises the vote on a table the player is not looking at.
 * When the money test was retrofitted to the prompt it was not propagated to the
 * fan, so every sitting-out seat was advertised a vote no panel would render —
 * and a phantom vote outranks every turn in the multi-table scroll queue. One
 * predicate, two callers, so that cannot drift again.
 */
export const hasStakeInHand = (
  game: Record<string, unknown> | undefined,
  seatId: number
): boolean => {
  const anteRaw = game?.['p' + seatId + 'ante'] as string | undefined;

  // `p{n}ante` reaches the client only from broadcast payloads built after the
  // processor projections were widened to select it. A client can deploy ahead
  // of those, and an ABSENT column is not the same fact as a zero one — treating
  // it as zero would hide the panel from a seat that is all-in for its ante
  // alone, which the server would have accepted. So when the field is missing,
  // fall back to the previous gate rather than tightening on data we do not have.
  if (anteRaw === undefined) return true;

  return (
    stakeAmount(game?.['p' + seatId + 'bet'] as string | undefined) > 0 || stakeAmount(anteRaw) > 0
  );
};
